import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  MdAdd,
  MdEdit,
  MdDelete,
  MdPerson,
  MdCheckCircle,
  MdBusiness,
  MdPeople,
  MdSearch,
  MdLock,
  MdVisibility,
  MdVisibilityOff,
} from "react-icons/md";
import ActionMenu from "../components/common/ActionMenu";
import api from "../services/api";
import socket from "../services/socket";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import { formatDistanceToNow } from "date-fns";

const API_URL =
  process.env.NODE_ENV === "production"
    ? (process.env.REACT_APP_API_URL || window.location.origin)
    : "http://localhost:6000";

const COUNTRY_CODES = [
  { code: "+1", label: "United States (+1)" },
  { code: "+44", label: "United Kingdom (+44)" },
  { code: "+61", label: "Australia (+61)" },
  { code: "+49", label: "Germany (+49)" },
  { code: "+33", label: "France (+33)" },
  { code: "+971", label: "United Arab Emirates (+971)" },
  { code: "+966", label: "Saudi Arabia (+966)" },
  { code: "+92", label: "Pakistan (+92)" },
  { code: "+91", label: "India (+91)" },
  { code: "+880", label: "Bangladesh (+880)" },
  { code: "+94", label: "Sri Lanka (+94)" },
  { code: "+93", label: "Afghanistan (+93)" },
  { code: "+81", label: "Japan (+81)" },
  { code: "+86", label: "China (+86)" },
  { code: "+60", label: "Malaysia (+60)" },
  { code: "+62", label: "Indonesia (+62)" },
  { code: "+63", label: "Philippines (+63)" },
  { code: "+65", label: "Singapore (+65)" },
  { code: "+27", label: "South Africa (+27)" },
  { code: "+234", label: "Nigeria (+234)" },
  { code: "+254", label: "Kenya (+254)" },
  { code: "+255", label: "Tanzania (+255)" },
  { code: "+20", label: "Egypt (+20)" },
  { code: "+34", label: "Spain (+34)" },
  { code: "+39", label: "Italy (+39)" },
  { code: "+7", label: "Russia (+7)" },
  { code: "+55", label: "Brazil (+55)" },
  { code: "+52", label: "Mexico (+52)" },
  { code: "+90", label: "Turkey (+90)" },
  { code: "+353", label: "Ireland (+353)" },
  { code: "+46", label: "Sweden (+46)" },
  { code: "+47", label: "Norway (+47)" },
  { code: "+45", label: "Denmark (+45)" },
  { code: "+31", label: "Netherlands (+31)" },
  { code: "+41", label: "Switzerland (+41)" },
  { code: "+48", label: "Poland (+48)" },
  { code: "+420", label: "Czech Republic (+420)" },
  { code: "+43", label: "Austria (+43)" },
];

const parsePhone = (phone) => {
  if (!phone) {
    return { countryCode: "+92", local: "" };
  }
  const trimmed = String(phone).trim();
  const match = trimmed.match(/^(\+\d{1,4})(.*)$/);
  if (match) {
    return {
      countryCode: match[1],
      local: match[2].replace(/^\s+/, "").replace(/^0+/, ""),
    };
  }
  return { countryCode: "+92", local: trimmed.replace(/^0+/, "") };
};
const getInitials2 = (name) =>
  name
    ?.split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
const UCOLORS = [
  "#6366f1",
  "#f59e0b",
  "#10b981",
  "#ef4444",
  "#3b82f6",
  "#8b5cf6",
];
const uAvatarColor = (name) =>
  UCOLORS[(name?.charCodeAt(0) || 0) % UCOLORS.length];
const UserAvatar = ({ user, size = 32 }) => {
  const src = user?.avatar
    ? user.avatar.startsWith("http")
      ? user.avatar
      : `${API_URL}${user.avatar}`
    : null;
  return (
    <div
      className="avatar"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.3,
        background: src ? "transparent" : uAvatarColor(user?.name),
        flexShrink: 0,
      }}
    >
      {src ? (
        <img
          src={src}
          alt={user?.name}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: "50%",
          }}
          onError={(e) => {
            e.target.style.display = "none";
            e.target.parentNode.textContent = getInitials2(user?.name);
          }}
        />
      ) : (
        getInitials2(user?.name)
      )}
    </div>
  );
};

// Normalize role for display (legacy 'user' -> 'staff')
const normRole = (r) => (r === "user" ? "staff" : r || "staff");

// ─── Password Field with show/hide toggle + strength bar ──────
const getStrength = (pwd) => {
  if (!pwd) return 0;
  let s = 0;
  if (pwd.length >= 6)  s++;
  if (pwd.length >= 10) s++;
  if (/[A-Z]/.test(pwd)) s++;
  if (/[0-9]/.test(pwd)) s++;
  if (/[^A-Za-z0-9]/.test(pwd)) s++;
  return s; // 0–5
};
const STRENGTH_LABEL = ["", "Very weak", "Weak", "Fair", "Strong", "Very strong"];
const STRENGTH_COLOR = ["", "#ef4444", "#f97316", "#eab308", "#22c55e", "#16a34a"];

const PasswordField = ({ label, value, onChange, required }) => {
  const [show, setShow] = useState(false);
  const strength = getStrength(value);
  return (
    <div className="form-group">
      <label className="form-label" style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <MdLock size={14} color="var(--primary)" /> {label}
      </label>
      <div style={{ position: "relative" }}>
        <input
          className="form-control"
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Min 6 characters"
          minLength={required ? 6 : 0}
          required={required}
          style={{ paddingRight: 40 }}
        />
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          style={{
            position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
            background: "none", border: "none", cursor: "pointer", padding: 0,
            color: "var(--text-muted)", display: "flex", alignItems: "center",
          }}
          aria-label={show ? "Hide password" : "Show password"}
        >
          {show ? <MdVisibilityOff size={18} /> : <MdVisibility size={18} />}
        </button>
      </div>
      {value && (
        <div style={{ marginTop: 6 }}>
          <div style={{ display: "flex", gap: 3, marginBottom: 3 }}>
            {[1,2,3,4,5].map((i) => (
              <div key={i} style={{
                flex: 1, height: 3, borderRadius: 2,
                background: i <= strength ? STRENGTH_COLOR[strength] : "var(--border)",
                transition: "background .2s",
              }} />
            ))}
          </div>
          <span style={{ fontSize: "0.72rem", color: STRENGTH_COLOR[strength], fontWeight: 600 }}>
            {STRENGTH_LABEL[strength]}
          </span>
        </div>
      )}
    </div>
  );
};

// ─── User Modal ───────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
const UserModal = ({ user, organizations, adminsList, onClose, onSaved }) => {
  const { user: currentUser } = useAuth();
  const currentUserRole = currentUser?.role === "user" ? "staff" : currentUser?.role;
  const isSuperAdmin = currentUserRole === "super_admin";
  const canAssignManagers = ["super_admin", "accounts", "hr"].includes(currentUserRole);
  const managersFromUser = (u) => {
    if (!u) return [];
    const fromManagers = (u.managers || []).map((m) => (m && m._id ? m._id : m)).filter(Boolean);
    const fromLegacy = u.managedBy ? [u.managedBy._id || u.managedBy] : [];
    return [...new Set([...fromManagers, ...fromLegacy])];
  };
  const [form, setForm] = useState(
    user
      ? {
          name: user.name,
          email: user.email,
          role: normRole(user.role),
          managers: managersFromUser(user),
          isActive: user.isActive,
          password: "",
          organization: user.organization?._id || "",
          designation: user.designation || "",
          phone: parsePhone(user.phone).local,
          phoneCountryCode: parsePhone(user.phone).countryCode,
        }
      : {
          name: "",
          email: "",
          password: "",
          role: "staff",
          managers: [],
          isActive: true,
          organization: "",
          designation: "",
          phone: "",
          phoneCountryCode: "+92",
        },
  );
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        role: form.role,
        isActive: form.isActive,
        organization: form.organization || null,
        designation: form.designation,
        phone: `${form.phoneCountryCode || "+92"}${form.phone || ""}`,
      };
      if (form.role === "staff") {
        payload.managers = canAssignManagers
          ? (form.managers || [])
          : user
            ? (form.managers || [])
            : [currentUser?._id].filter(Boolean);
      } else {
        payload.managers = [];
      }
      if (user) {
        if (form.password) payload.password = form.password;
        const { data: putRes } = await api.put(`/users/${user._id}`, payload);
        let msg = "User updated";
        if (
          typeof putRes.tasksReassignedToManager === "number" &&
          form.role === "staff" &&
          user.isActive !== false &&
          form.isActive === false
        ) {
          if (putRes.tasksReassignedToManager > 0) {
            msg = `User updated. ${putRes.tasksReassignedToManager} task(s) moved to their manager.`;
          } else if (putRes.taskReassignmentSkippedReason === "no_tagged_manager") {
            msg =
              "User updated. No tagged manager — tasks were not reassigned.";
          }
        }
        toast.success(msg);
      } else {
        const { data } = await api.post("/auth/register", {
          name: form.name,
          email: form.email,
          password: form.password,
        });
        await api.put(`/users/${data.user._id}`, payload);
        toast.success("User added");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error saving");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal"
        style={{ maxWidth: 560 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3>{user ? "Edit Staff Member" : "Add Staff Member"}</h3>
          <button className="btn-icon" onClick={onClose}>
            ✕
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="grid grid-2">
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input
                  className="form-control"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="John Doe"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  className="form-control"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="john@example.com"
                  required
                />
              </div>
            </div>
            {/* Password field — always shown for new users; only super_admin can change existing user passwords */}
            {(!user || isSuperAdmin) && (
              <PasswordField
                label={user ? "Change Password (leave blank to keep)" : "Password *"}
                value={form.password}
                onChange={(v) => setForm({ ...form, password: v })}
                required={!user}
              />
            )}

            <div
              style={{
                background: "var(--surface2)",
                borderRadius: 10,
                padding: "14px 16px",
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  fontWeight: 700,
                  fontSize: "0.78rem",
                  textTransform: "uppercase",
                  letterSpacing: ".5px",
                  color: "var(--text-muted)",
                  marginBottom: 12,
                }}
              >
                Organization Info
              </div>
              <div className="form-group">
                <label className="form-label">Organization</label>
                <select
                  className="form-control"
                  value={form.organization}
                  onChange={(e) =>
                    setForm({ ...form, organization: e.target.value })
                  }
                >
                  <option value="">— No Organization —</option>
                  {organizations.map((o) => (
                    <option key={o._id} value={o._id}>
                      {o.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-2">
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Designation</label>
                  <input
                    className="form-control"
                    value={form.designation}
                    onChange={(e) =>
                      setForm({ ...form, designation: e.target.value })
                    }
                    placeholder="e.g. Manager"
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Phone</label>
                  <div style={{ display: "flex", gap: 8 }}>
                    <select
                      className="form-control"
                      style={{ maxWidth: 150 }}
                      value={form.phoneCountryCode}
                      onChange={(e) =>
                        setForm({ ...form, phoneCountryCode: e.target.value })
                      }
                    >
                      {COUNTRY_CODES.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                    <input
                      className="form-control"
                      value={form.phone}
                      onChange={(e) =>
                        setForm({ ...form, phone: e.target.value })
                      }
                      placeholder="3001234567"
                    />
                  </div>
                  <div
                    className="text-muted"
                    style={{ fontSize: "0.7rem", marginTop: 4 }}
                  >
                    Select country code and enter number without leading 0.
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-2">
              <div className="form-group">
                <label className="form-label">System Role</label>
                <select
                  className="form-control"
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value, managers: e.target.value === "staff" ? form.managers : [] })}
                >
                  <option value="staff">Staff</option>
                  <option value="admin">Admin</option>
                  <option value="accounts">Accounts</option>
                  <option value="hr">HR</option>
                  {isSuperAdmin && <option value="super_admin">Super Admin</option>}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select
                  className="form-control"
                  value={String(form.isActive)}
                  onChange={(e) =>
                    setForm({ ...form, isActive: e.target.value === "true" })
                  }
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
            </div>
            {form.role === "staff" && canAssignManagers && adminsList && adminsList.length > 0 && (
              <div className="form-group" style={{ marginTop: 8 }}>
                <label className="form-label">Managers (multiple admins can manage this staff)</label>
                <select
                  className="form-control"
                  multiple
                  value={form.managers || []}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions, (o) => o.value);
                    setForm({ ...form, managers: selected });
                  }}
                  style={{ minHeight: 100 }}
                >
                  {adminsList.map((a) => (
                    <option key={a._id} value={a._id}>
                      {a.name} ({a.role === "super_admin" ? "Super Admin" : a.role === "admin" ? "Admin" : a.role === "accounts" ? "Accounts" : "HR"})
                    </option>
                  ))}
                </select>
                <div className="text-muted" style={{ fontSize: "0.75rem", marginTop: 4 }}>
                  Hold Ctrl/Cmd to select multiple.
                </div>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? "Saving..." : user ? "Update Staff" : "Add Staff"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────
const AdminStaff = () => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterOrg, setFilterOrg] = useState("");
  const [search, setSearch] = useState("");

  const currentRole = currentUser?.role === "user" ? "staff" : currentUser?.role;
  const _adminsList =
    currentRole === "super_admin"
      ? users.filter((u) =>
          ["admin", "super_admin", "accounts", "hr"].includes(u.role),
        )
      : [];

  const fetchAll = async (silent = false) => {
    const params = {};
    if (filterOrg) params.orgId = filterOrg;
    try {
      const [u, o] = await Promise.all([
        api.get("/users", { params }),
        api.get("/organizations"),
      ]);
      setUsers(u.data.data);
      setOrganizations(o.data.data);
    } catch (err) {
      console.error("AdminStaff fetchAll error", err);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterOrg]);

  const deleteUser = async (id) => {
    if (!window.confirm("Delete this staff member?")) return;
    await api.delete(`/users/${id}`);
    toast.success("Staff deleted");
    fetchAll();
  };

  const toggleRole = async (u) => {
    const next = u.role === "staff" ? "admin" : "staff";
    await api.put(`/users/${u._id}`, { role: next, managers: next === "staff" ? [currentUser?._id].filter(Boolean) : [] });
    toast.success(`Role changed to ${next}`);
    fetchAll();
  };

  const toggleActive = async (user) => {
    const wasActive = user.isActive !== false;
    const { data } = await api.put(`/users/${user._id}`, {
      isActive: !wasActive,
    });
    const roleKey = user.role === "user" ? "staff" : user.role;
    if (wasActive && typeof data.tasksReassignedToManager === "number" && roleKey === "staff") {
      if (data.tasksReassignedToManager > 0) {
        toast.success(
          `Staff deactivated. ${data.tasksReassignedToManager} task(s) moved to their manager.`,
        );
      } else if (data.taskReassignmentSkippedReason === "no_tagged_manager") {
        toast.success(
          "Staff deactivated. No tagged manager — tasks were not reassigned.",
        );
      } else {
        toast.success("Staff deactivated.");
      }
    } else {
      toast.success("Status updated");
    }
    fetchAll();
  };

  // Realtime presence updates from Socket.IO
  useEffect(() => {
    const handlePresence = (payload) => {
      const { userId, isOnline, lastSeen } = payload || {};
      if (!userId) return;
      setUsers((prev) =>
        prev.map((u) =>
          String(u._id) === String(userId)
            ? {
                ...u,
                isOnline: !!isOnline,
                ...(lastSeen !== undefined ? { lastSeen } : {}),
              }
            : u
        )
      );
    };

    // Server restarted and finished cleanup — re-fetch after a short delay
    const handleServerReady = () => setTimeout(() => fetchAll(true), 1500);

    socket.on("presence:update", handlePresence);
    socket.on("server:ready",    handleServerReady);
    return () => {
      socket.off("presence:update", handlePresence);
      socket.off("server:ready",    handleServerReady);
    };
  }, []); // eslint-disable-line

  // Periodic re-sync every 20 s to keep presence fresh
  useEffect(() => {
    const id = setInterval(() => fetchAll(true), 20_000);
    return () => clearInterval(id);
  }, []);  // eslint-disable-line

  // Re-sync when the browser tab becomes visible again
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") fetchAll(true);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []); // eslint-disable-line

  const filtered = users.filter(
    (u) =>
      !search ||
      u.name.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase()),
  );

  const activeCount = users.filter((u) => u.isActive).length;
  const adminCount = users.filter((u) => u.role === "admin" || u.role === "super_admin").length;

  const formatLastSeen = (dateStr) => {
    if (!dateStr) return "Last seen: —";
    try {
      const d = new Date(dateStr);
      if (Number.isNaN(d.getTime())) return "Last seen: —";
      return `Last seen: ${formatDistanceToNow(d, { addSuffix: true })}`;
    } catch {
      return "Last seen: —";
    }
  };

  if (loading)
    return (
      <div className="loading">
        <div className="spinner" />
      </div>
    );

  return (
    <main className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Staff Management</h1>
          <p className="page-subtitle">
            Manage all staff members across organizations
          </p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => navigate("/admin/staff/new")}
        >
          <MdAdd /> Add Staff
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-4" style={{ marginBottom: 24 }}>
        {[
          {
            label: "Total Staff",
            value: users.length,
            color: "#6366f1",
            bg: "#e0e7ff",
          },
          {
            label: "Active",
            value: activeCount,
            color: "#10b981",
            bg: "#d1fae5",
          },
          {
            label: "Inactive",
            value: users.length - activeCount,
            color: "#64748b",
            bg: "#f1f5f9",
          },
          {
            label: "Admins",
            value: adminCount,
            color: "#ef4444",
            bg: "#fee2e2",
          },
        ].map((s) => (
          <div key={s.label} className="card stat-card">
            <div className="stat-icon" style={{ background: s.bg }}>
              <MdPeople color={s.color} size={22} />
            </div>
            <div className="stat-value">{s.value}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div
        style={{
          display: "flex",
          gap: 10,
          marginBottom: 20,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            background: "var(--surface)",
            border: "1.5px solid var(--border)",
            borderRadius: 8,
            padding: "0 12px",
            flex: 1,
            maxWidth: 280,
          }}
        >
          <MdSearch color="var(--text-muted)" />
          <input
            style={{
              border: "none",
              outline: "none",
              padding: "9px 0",
              fontSize: "0.875rem",
              width: "100%",
              background: "transparent",
            }}
            placeholder="Search staff..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="form-control"
          style={{ width: "auto" }}
          value={filterOrg}
          onChange={(e) => setFilterOrg(e.target.value)}
        >
          <option value="">All Organizations</option>
          {organizations.map((o) => (
            <option key={o._id} value={o._id}>
              {o.name}
            </option>
          ))}
        </select>
        {(filterOrg || search) && (
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => {
              setFilterOrg("");
              setSearch("");
            }}
          >
            Clear
          </button>
        )}
        <span
          className="text-muted"
          style={{ marginLeft: "auto", fontSize: "0.82rem" }}
        >
          {filtered.length} result{filtered.length !== 1 ? "s" : ""}
        </span>
      </div>

      <div className="card">
        {filtered.length === 0 ? (
          <div className="empty-state">
            <MdPeople size={48} />
            <h3>No staff found</h3>
            <p>Try adjusting your filters or add a new staff member.</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Staff Member</th>
                  <th>Organization</th>
                  <th>Designation</th>
                  <th>Phone</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => (
                  <tr key={u._id}>
                    <td>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                        }}
                      >
                        <div style={{ position: "relative" }}>
                          <UserAvatar user={u} size={32} />
                          <span
                            style={{
                              position: "absolute",
                              right: -2,
                              bottom: -2,
                              width: 10,
                              height: 10,
                              borderRadius: "50%",
                              border: "2px solid #fff",
                              backgroundColor: u.isOnline ? "#22c55e" : "#9ca3af",
                            }}
                          />
                        </div>
                        <div>
                          <div style={{ fontWeight: 600 }}>{u.name}</div>
                          <div
                            className="text-muted"
                            style={{ fontSize: "0.75rem" }}
                          >
                            {u.email}
                          </div>
                          <div
                            className="text-muted"
                            style={{ fontSize: "0.7rem", marginTop: 2 }}
                          >
                            {u.isOnline ? "Online now" : formatLastSeen(u.lastSeen)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      {u.organization ? (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <MdBusiness size={14} color="var(--primary)" />
                          <span
                            style={{ fontSize: "0.875rem", fontWeight: 500 }}
                          >
                            {u.organization.name}
                          </span>
                        </div>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="text-muted">{u.designation || "—"}</td>
                    <td className="text-muted">{u.phone || "—"}</td>
                    <td>
                      <span
                        className={`badge ${
                          u.role === "super_admin"
                            ? "badge-critical"
                            : u.role === "admin"
                              ? "badge-high"
                              : u.role === "accounts"
                                ? "badge-medium"
                                : u.role === "hr"
                                  ? "badge-low"
                                  : "badge-todo"
                        }`}
                      >
                        {u.role === "super_admin"
                          ? "Super Admin"
                          : u.role === "admin"
                            ? "Admin"
                            : u.role === "accounts"
                              ? "Accounts"
                              : u.role === "hr"
                                ? "HR"
                                : "Staff"}
                      </span>
                      {u.role === "staff" && (
                        (() => {
                          const managers = [...(u.managers || []), ...(u.managedBy ? [u.managedBy] : [])];
                          const names = [...new Map(managers.map((m) => [m?._id || m, m])).values()]
                            .filter(Boolean)
                            .map((m) => (typeof m === "object" ? m.name : null))
                            .filter(Boolean);
                          return names.length ? (
                            <div className="text-muted" style={{ fontSize: "0.7rem", marginTop: 2 }}>
                              Managed by: {names.join(", ")}
                            </div>
                          ) : null;
                        })()
                      )}
                    </td>
                    <td>
                      <span
                        className={`badge ${u.isActive ? "badge-done" : "badge-review"}`}
                      >
                        {u.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <ActionMenu
                        items={[
                          {
                            label: "Edit Staff",
                            icon: <MdEdit size={15} />,
                              onClick: () => navigate(`/admin/staff/${u._id}/edit`),
                          },
                          ...(currentRole === "super_admin"
                            ? [{
                                label: u.role === "staff" ? "Make Admin" : "Make Staff",
                                icon: <MdPerson size={15} />,
                                onClick: () => toggleRole(u),
                              }]
                            : []),
                          {
                            label: u.isActive ? "Deactivate" : "Activate",
                            icon: <MdCheckCircle size={15} />,
                            onClick: () => toggleActive(u),
                          },
                          "divider",
                          {
                            label: "Delete Staff",
                            icon: <MdDelete size={15} />,
                            onClick: () => deleteUser(u._id),
                            danger: true,
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </main>
  );
};

export default AdminStaff;
